import React, { useEffect, useMemo, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, IQaRegistrationState, IMicrositeListResponse, MicrositeActionCreators, ICenterListResponse, CenterActionCreators, CenterEmailType, IBasicCenterListResponse } from '@nkr/store/index';
import { IBaseResponse, ICenter, ICenterBase } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, IHeaderParams, ICellRendererParams, FirstDataRenderedEvent } from 'ag-grid-community';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Col, DropdownButton, FormControl, MenuItem, Row } from 'react-bootstrap';
import { GridWrapper } from '@nkr/views/admin/crm/products/styles';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import OptionsModal from './options-modal';

const CenterGridWrapper = styled(GridWrapper)`
    .action-menu {
        line-height: normal;
    }

    .action-menu > .ag-react-container {
        line-height: normal;
        display: flex;
        justify-content: center;
    }
`;

interface IProps { }

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenters: () => Promise<IBasicCenterListResponse>,
    sendEmail: (id: number, emailType: CenterEmailType, additionalOptions: { historyCompletedDays: number }) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const CenterList = (props: TProps) => {
    const [list, setList] = useState<ICenterBase[]>([]);
    const [centerType, setCenterType] = useState(1);
    const [emailType, setEmailType] = useState(0);
    const [showOptions, setShowOptions] = useState(false);
    const [historyCompletedDays, setHistoryCompletedDays] = useState(1);
    const [centerId, setCenterId] = useState(0);
    const [search, setSearch] = useState('');

    const getData = () => {
        props.showLoading('Getting centers, one moment please...').then(() => {
            props.getCenters().then(response => {
                if (response.data.success) {
                    setList(response.data.results);
                } else {

                }

                props.hideLoading();
            });
        });
    };

    const handleCenterTypeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        setCenterType(parseInt(e.currentTarget.value));
    };

    const handleSearchChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        setSearch(e.currentTarget.value);
    };

    const handleActionSelect = (id: number, emailType: CenterEmailType) => {
        setCenterId(id);
        setEmailType(emailType);

        if (emailType != CenterEmailType.MicrositeDailySummary) {
            props.showLoading('Sending email, one moment please...').then(() => {
                props.sendEmail(id, emailType, { historyCompletedDays })
                    .then(response => {
                        if (response.data.success) {
                            toast.success('Email sent successfully');
                        }

                        props.hideLoading();
                    })
                    .catch(error => {
                        props.hideLoading();
                        console.log(error);
                    });
            });
        } else {
            setShowOptions(true);
        }
    };

    const handleOptionsFieldChange = (name: string, value: any) => {
        if (name == 'historyCompletedDays') {
            setHistoryCompletedDays(parseInt(value));
        }
    };

    const handleSubmitWithOptions = () => {
        setShowOptions(false);

        props.showLoading('Sending email, one moment please...').then(() => {
            props.sendEmail(centerId, emailType, { historyCompletedDays })
                .then(response => {
                    if (response.data.success) {
                        toast.success('Email sent successfully');
                    }

                    props.hideLoading();
                })
                .catch(error => {
                    props.hideLoading();
                    console.log(error);
                });
        });
    };

    const handleCancelOptions = () => {
        setShowOptions(false);
    };

    useEffect(() => {
        getData();
    }, []);

    const columns: ColDef[] = [
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Rank</SortableHeaderComponent>,
            headerName: 'Rank',
            field: 'id',
            width: 60,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            cellRendererFramework: RankRenderer
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Alias</SortableHeaderComponent>,
            headerName: 'Alias',
            field: 'alias',
            width: 150,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Name</SortableHeaderComponent>,
            headerName: 'Name',
            field: 'name',
            width: 150,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        // {
        //     headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Email</SortableHeaderComponent>,
        //     headerName: 'Email',
        //     field: 'email',
        //     width: 150,
        //     cellClass: 'list-cell',
        //     headerClass: 'list-header',
        //     sortable: true
        // },
        // {
        //     headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
        //     headerName: 'Center',
        //     field: 'center.alias',
        //     width: 100,
        //     cellClass: 'list-cell',
        //     headerClass: 'list-header',
        //     sortable: true
        // },
        {
            headerName: '',
            field: 'id',
            width: 40,
            cellClass: 'action-menu',
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ICenter;

                return (
                    <DropdownButton bsStyle='primary' bsSize='xs' pullRight title='Actions' id={'actions-' + data.id} onSelect={(e: any) => handleActionSelect(data.id, e)}>
                        <MenuItem eventKey={CenterEmailType.CenterPerformance}>Center Performance</MenuItem>
                        <MenuItem eventKey={CenterEmailType.ActionCenterDigest}>Action Center Digest</MenuItem>
                        <MenuItem eventKey={CenterEmailType.MicrositeDailySummary}>Microsite Daily Summary</MenuItem>
                    </DropdownButton>
                );
            }
        }
    ];

    const columnDefs = useMemo(() => columns, []);

    const filteredList = list.filter(x => {
        let centerTypeFilter = centerType > 0 ? x.isMemberCenter == (centerType == 1) : true;
        let searchFilter = search.length >= 3 ? x.alias.toLowerCase().indexOf(search.toLowerCase()) >= 0 || x.name.toLowerCase().indexOf(search.toLowerCase()) >= 0 : true;
        return centerTypeFilter && searchFilter;
    });

    return (
        <>
            <div className='container-fluid'>
                <Row>
                    <Col xs={12} lg={12}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 30, marginBottom: 20 }}>
                            <div style={{ fontSize: 28, fontWeight: 500, flexGrow: 2 }}>
                                QA - Centers
                            </div>
                            <div style={{ flexGrow: 1, marginRight: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Center&nbsp;Type:</div>
                                    <FormControl
                                        componentClass='select'
                                        value={centerType}
                                        onChange={handleCenterTypeChange}
                                    >
                                        <option value={0}>- All Centers -</option>
                                        <option value={1}>Member Centers</option>
                                        <option value={2}>Prospect Centers</option>
                                    </FormControl>
                                </div>
                            </div>
                            <div style={{ flexGrow: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Search:</div>
                                    <FormControl
                                        placeholder='Start typing to search by alias/name'
                                        value={search}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <CenterGridWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={filteredList}
                        rowHeight={35}
                        suppressCellSelection={true}
                        suppressRowClickSelection={true}
                        onFirstDataRendered={(props: FirstDataRenderedEvent) => {
                            props.api.sizeColumnsToFit();
                        }}
                    >
                    </AgGridReact>
                </CenterGridWrapper>
            </div>

            <OptionsModal
                show={showOptions}
                emailType={emailType}
                historyCompletedDays={historyCompletedDays}
                onFieldChange={handleOptionsFieldChange}
                onSubmit={handleSubmitWithOptions}
                onCancel={handleCancelOptions} />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        registrationSteps: appState.registrationSteps
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCenters: () => {
            return dispatch(CenterActionCreators.getBasicCenterList(false));
        },
        sendEmail: (id, emailType, additionalOptions) => {
            return dispatch(CenterActionCreators.sendEmail(id, emailType, additionalOptions));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterList);